import {
    AppliedFiltersDetailsBody,
    ISelectedFiltersDetails,
} from '../../../components/shared/filters/AppliedFiltersDetailsBody';

import { AppliedFiltersSection } from '../../../components/shared/filters/AppliedFiltersSection';
import { AppliedFiltersView } from '../../../components/shared/filters/AppliedFiltersView';
import { CheckmateDialog } from '../../../components/shared/dialog';
import { FilterBadge } from '../../../components/shared/filters/FilterBadge';
import { IPackageSettlementSearchModel } from '../../../interfaces/IPackageSettlement';
import { LookupDataEnums } from '../../../utilities/Constants';
import { Stack } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { useGetLookupData } from '../../../shared/react-query-hooks/useGetLookupData';
import { useGetPlaintiffsFirms } from '../../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useState } from 'react';

export interface IPackageListAppliedFiltersProps {
    onApplyFilters: (searchModel: IPackageSettlementSearchModel) => void;
    onToggleFilterPane: () => void;
    searchModel: IPackageSettlementSearchModel;
}

export function PackageListAppliedFilters(props: IPackageListAppliedFiltersProps) {
    const packageDealStatuses = useGetLookupData(LookupDataEnums.PackageSettlementStatusTypes);
    const plaintiffsFirms = useGetPlaintiffsFirms();

    const [showFilterDetails, setShowFilterDetails] = useState(false);

    const handleToggleFilterDetailsModal = () => {
        setShowFilterDetails(true);
    };

    const handleRemoveStatus = (statusId: number) => {
        const updatedSearchModel = {
            ...props.searchModel,
            statusIds: props.searchModel.statusIds?.filter((id) => id !== statusId),
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemovePlaintiffsFirm = (plaintiffsFirmId: string) => {
        const updatedSearchModel = {
            ...props.searchModel,
            plaintiffsFirmIds: props.searchModel.plaintiffsFirmIds?.filter(
                (id) => id !== plaintiffsFirmId
            ),
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveStartDate = () => {
        const updatedSearchModel = {
            ...props.searchModel,
            startDate: undefined,
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveEndDate = () => {
        const updatedSearchModel = {
            ...props.searchModel,
            endDate: undefined,
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const sortedStatuses =
        props.searchModel.statusIds
            ?.map((statusId) => packageDealStatuses.data?.find((status) => status.id === statusId))
            .filter((status) => status !== undefined)
            .sort() ?? [];

    const sortedPlaintiffsFirms =
        props.searchModel.plaintiffsFirmIds
            ?.map((plaintiffsFirmId) =>
                plaintiffsFirms.data?.find((pf) => pf.guid === plaintiffsFirmId)
            )
            .filter((plaintiffsFirm) => plaintiffsFirm !== undefined)
            .sort() ?? [];

    const details: ISelectedFiltersDetails[] = [
        {
            label: 'Status',
            values: sortedStatuses?.map((s) => s?.displayName ?? s?.name ?? '') ?? [],
        },
        {
            label: 'Plaintiffs Firms',
            values: sortedPlaintiffsFirms?.map((pf) => pf?.name ?? '') ?? [],
        },
        {
            label: 'Start Date',
            values: props.searchModel.startDate ? [props.searchModel.startDate] : [],
        },
        { label: 'End Date', values: props.searchModel.endDate ? [props.searchModel.endDate] : [] },
    ];

    const noFiltersAreApplied =
        isEmpty(props.searchModel.statusIds) &&
        isEmpty(props.searchModel.plaintiffsFirmIds) &&
        !props.searchModel.startDate &&
        !props.searchModel.endDate;

    return (
        <AppliedFiltersSection onToggleFilterPane={props.onToggleFilterPane}>
            <AppliedFiltersView selectedFilterDetails={details} hideSelectedFiltersIcon={noFiltersAreApplied}>
                {sortedStatuses.map((status) => (
                    <FilterBadge
                        key={'status-filter-' + status?.id}
                        label={status?.displayName ?? status?.name ?? ''}
                        onRemove={() => handleRemoveStatus(status.id)}
                        isLoading={packageDealStatuses.isFetching}
                    />
                ))}

                {sortedPlaintiffsFirms.map((plaintiffsFirm) => (
                    <FilterBadge
                        key={'plaintiffs-firm-filter-' + plaintiffsFirm?.guid}
                        label={
                            plaintiffsFirms.data?.find((pf) => pf.guid === plaintiffsFirm.guid)
                                ?.name
                        }
                        onRemove={() => handleRemovePlaintiffsFirm(plaintiffsFirm.guid ?? '')}
                        isLoading={plaintiffsFirms.isFetching}
                    />
                ))}

                {props.searchModel.startDate && (
                    <FilterBadge
                        key={'start-date-filter'}
                        label={props.searchModel.startDate}
                        onRemove={handleRemoveStartDate}
                    />
                )}

                {props.searchModel.endDate && (
                    <FilterBadge
                        key={'end-date-filter'}
                        label={props.searchModel.endDate}
                        onRemove={handleRemoveEndDate}
                    />
                )}
            </AppliedFiltersView>
        </AppliedFiltersSection>
    );
}
